import { Facebook, Instagram, Mail, Lock, MessageCircle, Youtube } from "lucide-react";
import { LOGO_URL, scrollToSection } from "../config";
import { useSite, waLink } from "../siteContext";
import { useLang } from "../i18n";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

export const Footer = () => {
  const settings = useSite();
  const { lang, s } = useLang();
  const links = [
    { id: "about", label: s.nav_about },
    { id: "courses", label: s.nav_courses },
    { id: "journey", label: s.nav_journey },
    { id: "testimonials", label: s.nav_testimonials },
    { id: "faq", label: s.nav_faq },
  ];
  return (
  <footer data-testid="site-footer" className="border-t border-luxury-border bg-luxury-black">
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
      <div>
        <img src={LOGO_URL} alt="TheMaya" className="h-10 w-auto mb-5" />
        <p className="text-luxury-gray text-sm leading-relaxed mb-6 max-w-xs">
          TheMaya — {s.footer_text}
        </p>
        {!(settings.hidden_elements || []).includes("footer_social") && (
        <div className="flex items-center gap-5" data-testid="footer-socials">
          {settings.facebook ? (
          <a data-testid="footer-facebook" href={settings.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-luxury-gray hover:text-luxury-gold transition-colors">
            <Facebook className="w-5 h-5" strokeWidth={1.5} />
          </a>
          ) : null}
          {settings.instagram ? (
          <a data-testid="footer-instagram" href={settings.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram — TheMaya Hub Dubai" title="TheMaya Hub Dubai" className="text-luxury-gray hover:text-luxury-gold transition-colors">
            <Instagram className="w-5 h-5" strokeWidth={1.5} />
          </a>
          ) : null}
          {settings.instagram2 ? (
          <a data-testid="footer-instagram-2" href={settings.instagram2} target="_blank" rel="noopener noreferrer" aria-label="Instagram — Maya Taher" title="Maya Taher" className="text-luxury-gray hover:text-luxury-gold transition-colors">
            <Instagram className="w-5 h-5" strokeWidth={1.5} />
          </a>
          ) : null}
          {settings.tiktok ? (
          <a data-testid="footer-tiktok" href={settings.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok — TheMaya" title="TikTok — TheMaya" className="text-luxury-gray hover:text-luxury-gold transition-colors">
            <TikTokIcon className="w-5 h-5" />
          </a>
          ) : null}
          {settings.youtube ? (
          <a data-testid="footer-youtube" href={settings.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="text-luxury-gray hover:text-luxury-gold transition-colors">
            <Youtube className="w-5 h-5" strokeWidth={1.5} />
          </a>
          ) : null}
          <a data-testid="footer-email" href={`mailto:${settings.email}`} aria-label="Email" className="text-luxury-gray hover:text-luxury-gold transition-colors">
            <Mail className="w-5 h-5" strokeWidth={1.5} />
          </a>
          <a data-testid="footer-whatsapp" href={waLink(settings, undefined, lang)} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="text-luxury-gray hover:text-luxury-gold transition-colors">
            <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
          </a>
        </div>
        )}
      </div>

      <div>
        <p className="text-luxury-gold text-sm tracking-[0.3em] mb-6">{lang === "ar" ? "روابط سريعة" : "QUICK LINKS"}</p>
        <div className="flex flex-col gap-3">
          {links.map((l) => (
            <button key={l.id} data-testid={`footer-link-${l.id}`} onClick={() => scrollToSection(l.id)}
              className="text-start text-luxury-gray hover:text-luxury-gold transition-colors text-sm w-fit">
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-luxury-gold text-sm tracking-[0.3em] mb-6">{lang === "ar" ? "تواصل/تواصلي معنا" : "GET IN TOUCH"}</p>
        <div className="flex flex-col gap-3 text-sm">
          <a href={`mailto:${settings.email}`} className="text-luxury-gray hover:text-luxury-gold transition-colors w-fit" dir="ltr">{settings.email}</a>
          {settings.support_email ? (
            <a data-testid="footer-support-email" href={`mailto:${settings.support_email}`} className="text-luxury-gray hover:text-luxury-gold transition-colors w-fit" dir="ltr">
              {settings.support_email} <span className="text-xs">({lang === "ar" ? "للدعم" : "Support"})</span>
            </a>
          ) : null}
          <a href={waLink(settings, undefined, lang)} target="_blank" rel="noopener noreferrer" className="text-luxury-gray hover:text-luxury-gold transition-colors w-fit" dir="ltr">+{settings.whatsapp}</a>
          <a data-testid="footer-admin-link" href="/admin" className="mt-3 flex items-center gap-2 text-luxury-gray hover:text-luxury-gold transition-colors text-sm border border-luxury-border rounded-full px-4 py-1.5 w-fit">
            <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
            {lang === "ar" ? "دخول المشرف" : "Admin"}
          </a>
        </div>
      </div>
    </div>
    <div className="border-t border-luxury-border">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-luxury-gray">
        <p>TheMaya © {new Date().getFullYear()} — {lang === "ar" ? "جميع الحقوق محفوظة — المحتوى والدورات والمواد ملكية فكرية محمية" : "All rights reserved — content, courses & materials are protected intellectual property"}</p>
        <p>{lang === "ar" ? "صُنع بحب وعناية" : "Crafted with love & care"}</p>
      </div>
    </div>
  </footer>
  );
};
